/**
 * @file Particules d'ambiance de saison du hub (petales au printemps, lucioles en ete, feuilles en automne, flocons en hiver) : au plus 40
 *       sprites (20 en eco), animes analytiquement dans une boite qui suit la camera (recalee sur la grille monde : aucun glissement), un seul
 *       InstancedMesh actif, aucune allocation par frame. Inactif hors du hub.
 */
import { Color, DynamicDrawUsage, InstancedBufferAttribute, InstancedMesh, Matrix4, MeshBasicMaterial, Quaternion, Vector3 } from 'three';
import type { Scene } from 'three';
import type { HubSeason } from '@config/index';
import { buildSprite } from '../ammo/AmmoShapes';
import type { SpriteShape } from '../ammo/AmmoShapes';
import { colorOf } from '../ammo/colors';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { ToonKit } from '../toon/toon';

const TAU = Math.PI * 2;
const MAX = 40;
/** Cote (u) de la boite d'ambiance centree sur la camera et hauteur de chute. */
const BOX = 36;
const SKY = 9;

interface SeasonLook {
  shape: SpriteShape;
  colors: readonly string[];
  size: number;
}

const LOOKS: Record<HubSeason, SeasonLook> = {
  spring: { shape: 'petal', colors: ['#ffc2da', '#ffffff', '#ffd9e8'], size: 0.16 },
  summer: { shape: 'sparkle', colors: ['#fff6a0', '#d4ff9a'], size: 0.14 },
  autumn: { shape: 'petal', colors: ['#e8883a', '#c9602f', '#f2c14e', '#d9482f'], size: 0.22 },
  winter: { shape: 'flake', colors: ['#ffffff', '#e4f2ff'], size: 0.15 },
};

const hash01 = (n: number): number => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};
const frac = (v: number): number => v - Math.floor(v);
/** Ramene `v` dans [-L/2, L/2). */
const wrapSym = (v: number, L: number): number => v - L * Math.floor(v / L + 0.5);

export class SeasonAmbience {
  private eco = false;
  private season: HubSeason | null = null;
  private shown: HubSeason | null = null;
  private readonly layers = new Map<SpriteShape, { mesh: InstancedMesh; colors: InstancedBufferAttribute }>();
  private material: MeshBasicMaterial | null = null;
  private readonly m = new Matrix4();
  private readonly q = new Quaternion();
  private readonly qRoll = new Quaternion();
  private readonly p = new Vector3();
  private readonly s = new Vector3();
  private readonly c = new Color();
  private readonly axisZ = new Vector3(0, 0, 1);

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
  ) {}

  setEco(eco: boolean): void {
    this.eco = eco;
    this.shown = null;
  }

  /** `season` = saison du hub (null hors hub : tout est masque). (fx, fz) = point vise par la camera (monde Three). */
  update(season: HubSeason | null, fx: number, fz: number, time: number, camQ: Quaternion): void {
    if (season !== this.season) {
      this.season = season;
      this.shown = null;
    }
    if (!season) {
      this.hide();
      return;
    }
    const look = LOOKS[season];
    const layer = this.layer(look.shape);
    if (this.shown !== season) {
      this.shown = season;
      for (const [shape, l] of this.layers) l.mesh.visible = shape === look.shape;
      // couleurs fixes par emplacement
      for (let i = 0; i < MAX; i++) {
        this.c.copy(colorOf(look.colors[i % look.colors.length] ?? '#ffffff'));
        this.c.toArray(layer.colors.array, i * 3);
      }
      layer.colors.needsUpdate = true;
    }
    const n = this.eco ? MAX / 2 : MAX;
    for (let i = 0; i < n; i++) {
      const sa = hash01(i + 1);
      const sb = hash01(i * 3.7 + 11);
      const sc = hash01(i * 7.3 + 29);
      let x: number;
      let y: number;
      let z: number;
      let size = look.size * (0.8 + 0.5 * sc);
      let roll: number;
      if (season === 'summer') {
        // lucioles : flottent pres du sol sur des courbes de Lissajous, scintillent
        x = fx + wrapSym(sa * BOX + Math.sin(time * (0.2 + 0.2 * sb) + sc * TAU) * 2.4 - fx, BOX);
        z = fz + wrapSym(sb * BOX + Math.cos(time * (0.17 + 0.2 * sa) + sa * TAU) * 2.4 - fz, BOX);
        y = 0.5 + 1.4 * (0.5 + 0.5 * Math.sin(time * (0.5 + 0.4 * sc) + sa * TAU));
        size *= 0.4 + 0.9 * Math.abs(Math.sin(time * 2.2 + sb * TAU));
        roll = time * 2 + i;
      } else {
        // chute lente avec balancement ; feuilles : plus vite, plus de lacet
        const fall = season === 'winter' ? 0.075 : season === 'autumn' ? 0.1 : 0.06;
        const sway = season === 'autumn' ? 1.3 : season === 'spring' ? 0.9 : 0.5;
        const ph = frac(sa + time * fall * (0.7 + 0.6 * sc));
        y = SKY * (1 - ph) + 0.1;
        const w = time * (0.7 + sb) + sc * TAU;
        x = fx + wrapSym(sb * BOX + time * 0.45 + Math.sin(w) * sway - fx, BOX);
        z = fz + wrapSym(sc * BOX + time * 0.15 + Math.cos(w * 0.8) * sway - fz, BOX);
        roll = time * (sb - 0.5) * (season === 'autumn' ? 4 : 2.5) + sa * 6;
        // se fond en arrivant au sol
        size *= Math.min(1, y * 3);
      }
      this.p.set(x, y, z);
      this.qRoll.setFromAxisAngle(this.axisZ, roll);
      this.q.copy(camQ).multiply(this.qRoll);
      this.s.set(size, size, size);
      this.m.compose(this.p, this.q, this.s);
      layer.mesh.setMatrixAt(i, this.m);
    }
    layer.mesh.count = n;
    layer.mesh.visible = true;
    layer.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const l of this.layers.values()) {
      this.scene.remove(l.mesh);
      l.mesh.dispose();
    }
    this.layers.clear();
  }

  // ------------------------------------------------------------------------------------------------------------------

  private hide(): void {
    for (const l of this.layers.values()) l.mesh.visible = false;
  }

  private layer(shape: SpriteShape): { mesh: InstancedMesh; colors: InstancedBufferAttribute } {
    let l = this.layers.get(shape);
    if (l) return l;
    this.material ??= this.kit.track(new MeshBasicMaterial({ color: 0xffffff, vertexColors: true }));
    const mesh = new InstancedMesh(
      this.geo.get(`fx:sprite:${shape}`, () => buildSprite(shape)),
      this.material,
      MAX,
    );
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    const colors = new InstancedBufferAttribute(new Float32Array(MAX * 3).fill(1), 3);
    colors.setUsage(DynamicDrawUsage);
    mesh.instanceColor = colors;
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.renderOrder = 2;
    this.scene.add(mesh);
    l = { mesh, colors };
    this.layers.set(shape, l);
    return l;
  }
}
